import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  FileText,
  User,
  FolderOpen,
  BarChart3,
  ClipboardList,
  Users,
  Bell,
  LogOut,
  X,
  CheckCircle2,
  Settings,
} from 'lucide-react';
import { userRoleData } from 'redux_store/slices/userRole';
import { roles } from 'constants/constant';
import { getNavItems } from 'components/nav-bar';
import { getUserDetails } from 'auth/auth';
import { performLogout } from 'utils/logout';
import { LanguageToggle } from 'components/LanguageToggle/LanguageToggle';
import {
  useGetNotificationsQuery,
  useUpdateNotificationsMutation,
} from 'redux_store/services/notifications';
import { formatTimeDifferenceUTC } from 'constants/updatedAt';
import { EventEnums, NotificationTypeMap } from 'pages/notification/constant';

const leadPathRegex = /^\/[^/]+\/lead(?:\/|$)/;

const tabDefinitions = {
  dashboard: {
    title: 'Dashboard',
    path: '/dashboard',
    labelKey: 'navigation:dashboard',
    Icon: LayoutGrid,
    isActive: (pathname) => pathname === '/dashboard' || pathname.startsWith('/dashboard/'),
  },
  pipeline: {
    title: 'Pipeline',
    path: '/pipeline',
    labelKey: 'navigation:pipeline',
    Icon: FileText,
    isActive: (pathname) =>
      pathname === '/pipeline' ||
      pathname.startsWith('/pipeline/') ||
      leadPathRegex.test(pathname),
  },
  profile: {
    title: 'Profile',
    path: '/profile',
    labelKey: 'navigation:profile',
    Icon: User,
    isActive: (pathname) => pathname === '/profile' || pathname.startsWith('/profile/'),
  },
  documents: {
    title: 'Documents',
    path: '/documents',
    labelKey: 'navigation:documents',
    Icon: FolderOpen,
    isActive: (pathname) => pathname === '/documents' || pathname.startsWith('/documents/'),
  },
  tools: {
    title: 'Tools',
    path: '/tools',
    labelKey: 'navigation:tools',
    Icon: BarChart3,
    isActive: (pathname) => pathname === '/tools' || pathname.startsWith('/tools/'),
  },
  reports: {
    title: 'Reports',
    path: '/reports',
    labelKey: 'navigation:reports',
    Icon: ClipboardList,
    isActive: (pathname) => pathname === '/reports' || pathname.startsWith('/reports/'),
  },
  admin: {
    title: 'Admin',
    path: '/admin',
    labelKey: 'navigation:admin',
    Icon: Users,
    isActive: (pathname) => pathname === '/admin' || pathname.startsWith('/admin/'),
  },
  mobileProfile: {
    title: 'Profile',
    path: '#',
    labelKey: 'navigation:profile',
    Icon: User,
    isActive: () => false,
  },
  mobileSettings: {
    title: 'Settings',
    path: '#',
    labelKey: 'navigation:settings',
    Icon: Settings,
    isActive: () => false,
  },
};

const roleTabs = {
  [roles.consumer]: [
    'dashboard',
    'pipeline',
    'profile',
    'documents',
    'tools',
    'mobileSettings',
  ],
  [roles.loanOfficer]: [
    'dashboard',
    'pipeline',
    'tools',
    'reports',
  ],
  [roles.organization]: ['dashboard', 'pipeline', 'tools', 'reports', 'admin', 'mobileProfile'],
  [roles.branchAdmin]: ['dashboard', 'pipeline', 'tools', 'reports', 'admin', 'mobileProfile'],
  [roles.superAdmin]: ['dashboard', 'pipeline', 'tools', 'reports', 'admin', 'mobileProfile'],
  [roles.loanProcessor]: ['dashboard', 'pipeline', 'tools'],
};

const MobileNavBar = () => {
  const { t } = useTranslation(['navigation', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const roleState = useSelector(userRoleData);
  const currentRole = roleState?.userRole || roles.consumer;
  const isConsumerMobile = currentRole === roles.consumer;
  const isB2BMobile = [
    roles.loanOfficer,
    roles.loanProcessor,
    roles.organization,
    roles.branchAdmin,
    roles.superAdmin,
  ].includes(currentRole);
  const useCompactPanel = isB2BMobile || isConsumerMobile;
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [userDetails, setUserDetails] = useState(() => getUserDetails());

  // Re-read user details from cache when role changes (signals auth completed)
  useEffect(() => {
    setUserDetails(getUserDetails());
  }, [currentRole]);

  const { firstName, lastName, userEmail } = userDetails;
  const displayName =
    `${firstName ?? ''} ${lastName ?? ''}`.trim() || t('common:unknownUser');
  const initials = [firstName, lastName]
    .filter(Boolean)
    .map((name) => name?.[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const showUnread = false;
  const {
    currentData: notificationData,
    isFetching: notificationFetching,
  } = useGetNotificationsQuery(
    { showUnread },
    {
      pollingInterval: 60000,
      skip: useCompactPanel,
    }
  );
  const [updateNotification] = useUpdateNotificationsMutation();
  const notificationList = notificationData?.data?.data ?? [];
  const unreadCount = notificationList.filter((item) => item.isRead === '0').length;

  const handleMarkAsRead = async (notificationId) => {
    await updateNotification({
      data: { notification_ids: [notificationId] },
    });
  };

  const handleNotificationClick = async (notification) => {
    const { typeId, applicationId, userNotificationId, isRead } = notification;
    if (isRead === '0') {
      await updateNotification({
        data: { notification_ids: [userNotificationId] },
      });
    }
    const eventType = NotificationTypeMap[typeId];
    if (eventType) {
      switch (eventType) {
        case EventEnums.CONSUMER_PROFILE_ID_CREATED:
          navigate('/profile');
          break;
        case EventEnums.CONSUMER_PROFILE_COMPLETED:
        case EventEnums.CONSUMER_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVED:
          navigate('/pipeline');
          break;
        case EventEnums.CONSUMER_NOT_ELIGIBLE:
        case EventEnums.CONSUMER_PRE_APPROVAL_REJECTION:
          navigate('/profile');
          break;
        case EventEnums.LOAN_OFFICER_CONSUMER_COMPLETED_ONBOARDING:
        case EventEnums.LOAN_OFFICER_PROFILE_COMPLETED:
        case EventEnums.LOAN_OFFICER_ADD_LEAD:
        case EventEnums.LOAN_OFFICER_ELIGIBLE:
        case EventEnums.LOAN_OFFICER_NOT_ELIGIBLE:
          navigate(`/pipeline/lead?applicationId=${applicationId}`);
          break;
        case EventEnums.LOAN_OFFICER_PRE_APPROVED:
        case EventEnums.LOAN_OFFICER_PRE_APPROVAL_REJECTION:
          navigate(`/pipeline/lead?applicationId=${applicationId}`);
          break;
        default:
          break;
      }
    }
    setShowNotifications(false);
    setIsProfilePanelOpen(false);
  };

  const tabs = useMemo(() => {
    const selectedRoleTabs = roleTabs[currentRole] || roleTabs[roles.consumer];

    if (currentRole !== roles.loanProcessor) {
      return selectedRoleTabs.map((tabKey) => ({
        ...tabDefinitions[tabKey],
        key: tabKey,
      }));
    }

    const allowedTitles = new Set(
      getNavItems(t, currentRole)
        .filter((item) => item.role?.includes(currentRole))
        .map((item) => item.title)
    );

    return selectedRoleTabs
      .filter((tabKey) => allowedTitles.has(tabDefinitions[tabKey].title))
      .map((tabKey) => ({
        ...tabDefinitions[tabKey],
        key: tabKey,
      }));
  }, [currentRole, t]);

  const splitIndex = Math.ceil(tabs.length / 2);
  const leftTabs = tabs.slice(0, splitIndex);
  const rightTabs = tabs.slice(splitIndex);
  const panelLabel = isConsumerMobile
    ? t('navigation:settings', { defaultValue: 'Settings' })
    : t('navigation:profile', { defaultValue: 'Profile' });

  const openMobileChat = () => {
    window.dispatchEvent(new CustomEvent('open-mobile-chat'));
  };

  const renderTabItem = (tab) => {
    const isPanelTab =
      tab.key === 'mobileProfile' || tab.key === 'mobileSettings';
    const isActive = isPanelTab
      ? isProfilePanelOpen || tab.isActive(location.pathname)
      : tab.isActive(location.pathname);
    const label =
      currentRole === roles.consumer && tab.key === 'pipeline'
        ? t('navigation:myApplication', { defaultValue: 'My Applications' })
        : t(tab.labelKey, {
            defaultValue: tab.title,
          });

    if (isPanelTab) {
      return (
        <button
          key={tab.key}
          type="button"
          onClick={() => setIsProfilePanelOpen((prev) => !prev)}
          className={`mx-[2px] my-[4px] flex min-h-[46px] flex-col items-center justify-center gap-1 rounded-2xl px-1 transition-all ${
            isActive
              ? 'text-[#3c588b]'
              : 'text-[#2f3546]'
          }`}
          aria-label={label}
          aria-current={isActive ? 'page' : undefined}
        >
          <div className="relative flex items-center justify-center">
            <tab.Icon
              className={`${
                isActive ? 'h-[23px] w-[23px] stroke-[2.5]' : 'h-[22px] w-[22px] stroke-[2]'
              }`}
            />
            {isActive && (
              <span className="absolute -right-[3px] -top-[2px] h-[7px] w-[7px] rounded-full bg-[#3c588b]" />
            )}
          </div>
          <span
            className={`max-w-[72px] overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-semibold leading-none transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {label}
          </span>
        </button>
      );
    }

    return (
      <Link
        key={tab.key}
        to={tab.path}
        onClick={() => setIsProfilePanelOpen(false)}
        className={`mx-[2px] my-[4px] flex min-h-[46px] flex-col items-center justify-center gap-1 rounded-2xl px-1 transition-all ${
          isActive
            ? 'text-[#3c588b]'
            : 'text-[#2f3546]'
        }`}
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className="relative flex items-center justify-center">
          <tab.Icon
            className={`${
              isActive ? 'h-[23px] w-[23px] stroke-[2.5]' : 'h-[22px] w-[22px] stroke-[2]'
            }`}
          />
          {isActive && (
            <span className="absolute -right-[3px] -top-[2px] h-[7px] w-[7px] rounded-full bg-[#3c588b]" />
          )}
        </div>
        <span
          className={`max-w-[72px] overflow-hidden text-ellipsis whitespace-nowrap text-[10px] font-semibold leading-none transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {label}
        </span>
      </Link>
    );
  };

  if (!tabs.length) {
    return null;
  }

  return (
    <>
      {isProfilePanelOpen && (
        <div className="fixed inset-0 z-[85]">
          <button
            type="button"
            className="absolute inset-0 bg-black/10"
            onClick={() => {
              setShowNotifications(false);
              setIsProfilePanelOpen(false);
            }}
            aria-label={t('common:buttons.close', { defaultValue: 'Close', keySeparator: false })}
          />
          <div className="absolute bottom-[calc(106px+env(safe-area-inset-bottom))] right-2 w-[calc(100vw-16px)] max-w-[300px] rounded-[20px] border border-[#d8dfeb] bg-white p-2.5 shadow-[0_20px_45px_-24px_rgba(15,23,42,0.45)]">
            <div className="mb-2.5 flex items-center justify-between">
              {!useCompactPanel ? (
                <button
                  type="button"
                  className="flex items-center gap-2 text-[#7a8097]"
                  onClick={() => setShowNotifications((prev) => !prev)}
                >
                  <span className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-[#3c588b]" />
                    )}
                  </span>
                  <span className="text-[12px] font-medium">
                    {t('navigation:notifications')}
                  </span>
                </button>
              ) : (
                <span className="text-[12px] font-semibold text-[#66708d]">
                  {panelLabel}
                </span>
              )}
              <button
                type="button"
                className="rounded-md p-1 text-[#7a8097] hover:bg-[#f3f5fa]"
                onClick={() => {
                  setShowNotifications(false);
                  setIsProfilePanelOpen(false);
                }}
              >
                <X className="h-4 w-4" />
                </button>
            </div>

            {!useCompactPanel && showNotifications && (
              <div className="mb-2.5 max-h-[180px] overflow-y-auto rounded-xl border border-[#e7ebf2] bg-[#fbfcff] p-2">
                {notificationFetching && (
                  <p className="py-2 text-[12px] text-[#7a8097]">
                    {t('common:loadingNotifications')}
                  </p>
                )}
                {!notificationFetching && notificationList.length === 0 && (
                  <p className="py-2 text-[12px] text-[#7a8097]">
                    {t('common:noNewNotifications')}
                  </p>
                )}
                {!notificationFetching &&
                  notificationList.map((notification) => {
                    const isUnread = notification.isRead === '0';
                    const subject = notification.subject || t('common:notification');
                    const message = notification.message || '';
                    const timeLabel = notification?.createdAt
                      ? formatTimeDifferenceUTC(notification.createdAt)
                      : '';
                    return (
                      <div
                        key={notification.userNotificationId}
                        className={`mb-1 rounded-lg px-2 py-1.5 ${isUnread ? 'bg-[#f2f6ff]' : 'bg-transparent'}`}
                      >
                        <button
                          type="button"
                          className="w-full text-left"
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="mb-0.5 flex items-center justify-between gap-2">
                            <p className="truncate text-[12px] font-semibold text-[#112968]">
                              {subject}
                            </p>
                            <span className="shrink-0 text-[10px] text-[#7a8097]">
                              {timeLabel}
                            </span>
                          </div>
                          <p className="line-clamp-2 text-[11px] text-[#7a8097]">
                            {message}
                          </p>
                        </button>
                        {isUnread && (
                          <button
                            type="button"
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-medium text-[#3c588b]"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleMarkAsRead(notification.userNotificationId);
                            }}
                          >
                            {t('common:buttons.markAsRead')}
                            <CheckCircle2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {!useCompactPanel && (
              <div className="mb-2.5 flex items-center justify-between gap-2 rounded-xl bg-[#f6f8fc] px-2.5 py-1.5">
                <span className="text-[12px] font-semibold text-[#66708d]">
                  {t('navigation:language')}
                </span>
                <LanguageToggle variant="sidebar" forceExpanded />
              </div>
            )}

            <div className="mb-2.5 flex items-center gap-2.5 rounded-xl border border-[#e7ebf2] bg-[#fbfcff] p-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#dfe5ef] bg-[#edf1f9] text-[20px] font-semibold text-[#3c588b]">
                {initials || 'A'}
              </div>
              <div className="min-w-0">
                <p className="truncate text-[12px] font-semibold leading-tight text-[#112968]">
                  {displayName}
                </p>
                <p className="truncate text-[12px] leading-tight text-[#7a8097] mt-0.5">
                  {userEmail || t('common:noEmailOnFile')}
                </p>
              </div>
            </div>

            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-[12px] font-medium text-[#6d7691] transition-colors hover:bg-[#f4f6fb]"
              onClick={performLogout}
            >
              <LogOut className="h-5 w-5" />
              <span>{t('common:buttons.signOut')}</span>
            </button>
          </div>
        </div>
      )}

      <nav className="fixed inset-x-0 bottom-0 z-[80] px-2 pb-[max(env(safe-area-inset-bottom),8px)]">
      <div className="mx-auto w-full rounded-[24px] border border-[#dce2ed] bg-white/98 shadow-[0_-8px_28px_-20px_rgba(15,23,42,0.45)] backdrop-blur-md">
        <div
          className="grid h-[82px] items-center px-1 pt-1"
          style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}
        >
          {leftTabs.map(renderTabItem)}
          <button
            type="button"
            onClick={openMobileChat}
            className="mx-[2px] my-[4px] flex min-h-[46px] flex-col items-center justify-center gap-1 rounded-2xl px-1 text-[#2f3546] transition-all"
            aria-label="Open chat"
          >
            <span className="flex h-[34px] w-[34px] items-center justify-center rounded-full bg-[#3c588b]">
              <img
                src="/fundmoreai.png"
                alt="Open chat"
                className="h-[21px] w-[21px] object-contain"
              />
            </span>
            <span className="text-[10px] font-semibold leading-none opacity-0">
              Chat
            </span>
          </button>
          {rightTabs.map(renderTabItem)}
        </div>
      </div>
      </nav>
    </>
  );
};

export default MobileNavBar;
